// src/components/sections/AIFeatures.jsx 
import React, { useState } from 'react';
import { Cpu, FileText, Clock, Shield, ArrowRight } from 'lucide-react';
import SectionTitle from '../ui/SectionTitle';
import Button from '../ui/Button';

const aiFeatures = [
  {
    id: 'case-analysis',
    title: 'Automated Case Analysis',
    description: 'Our AI system can analyze case documents, identify relevant precedents, and extract key insights within minutes, not days.',
    icon: <FileText className="h-10 w-10 text-primary" />,
    color: 'bg-primary-50'
  },
  {
    id: 'legal-research',
    title: 'AI-Powered Legal Research',
    description: 'Quickly search through thousands of cases, statutes, and legal resources to find the most relevant information for your case.',
    icon: <Cpu className="h-10 w-10 text-primary" />,
    color: 'bg-primary-50'
  },
  {
    id: 'contract-analysis',
    title: 'Contract Review & Analysis',
    description: 'Automatically identify risks, unusual clauses, and potential issues in contracts with our advanced AI contract review system.',
    icon: <Shield className="h-10 w-10 text-primary" />,
    color: 'bg-primary-50'
  },
  {
    id: 'litigation-prediction',
    title: 'Litigation Outcome Prediction',
    description: 'Use data-driven insights to estimate case outcomes and develop more effective legal strategies with greater confidence.',
    icon: <Clock className="h-10 w-10 text-primary" />,
    color: 'bg-primary-50'
  }
];

const AIFeatures = () => {
  const [activeTab, setActiveTab] = useState('case-analysis');

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Updated title section to ensure proper spacing */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl font-serif mb-4">
            AI-Powered Legal Services
          </h2>
          <div className="w-20 h-1 bg-secondary mx-auto"></div>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            How we leverage artificial intelligence to deliver faster, more accurate, and more cost-effective legal solutions
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-12">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl overflow-hidden shadow-md">
              <div className="divide-y divide-gray-200">
                {aiFeatures.map((feature) => (
                  <button
                    key={feature.id}
                    onClick={() => setActiveTab(feature.id)}
                    className={`w-full px-6 py-5 text-left transition-colors ${
                      activeTab === feature.id 
                        ? 'bg-primary-50 border-l-4 border-primary' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center">
                      <div className={`rounded-lg p-3 ${feature.color}`}>
                        {feature.icon}
                      </div>
                      <div className="ml-4">
                        <h3 className={`font-medium ${
                          activeTab === feature.id ? 'text-primary' : 'text-gray-900'
                        }`}>
                          {feature.title}
                        </h3>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          <div className="lg:col-span-3">
            {aiFeatures.map((feature) => (
              activeTab === feature.id && (
                <div 
                  key={feature.id}
                  className="bg-white rounded-xl overflow-hidden shadow-md h-full"
                >
                  <div className="p-8">
                    <div className={`inline-flex rounded-lg p-3 ${feature.color} mb-6`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                    <p className="text-gray-600 text-lg mb-6">{feature.description}</p>
                    
                    {feature.id === 'case-analysis' && (
                      <div className="mt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">How It Works</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary text-sm font-medium">1</span>
                            <span className="ml-3 text-gray-600">Upload your case documents to our secure platform</span>
                          </li>
                          <li className="flex items-start">
                            <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary text-sm font-medium">2</span>
                            <span className="ml-3 text-gray-600">Our AI system analyzes the content, identifying key facts and legal issues</span>
                          </li>
                          <li className="flex items-start">
                            <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary text-sm font-medium">3</span>
                            <span className="ml-3 text-gray-600">The system searches for relevant precedents and statutes</span>
                          </li>
                          <li className="flex items-start">
                            <span className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-primary-100 text-primary text-sm font-medium">4</span>
                            <span className="ml-3 text-gray-600">Receive a comprehensive analysis report with actionable insights</span>
                          </li>
                        </ul>
                      </div>
                    )}
                    
                    {feature.id === 'legal-research' && (
                      <div className="mt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Key Benefits</h4>
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <span className="flex-shrink-0 text-primary">✓</span>
                            <span className="ml-3 text-gray-600">Reduce research time by up to 70%</span>
                          </li>
                          <li className="flex items-start">
                            <span className="flex-shrink-0 text-primary">✓</span>
                            <span className="ml-3 text-gray-600">Comprehensive search across Indian and international legal databases</span>
                          </li>
                          <li className="flex items-start">
                            <span className="flex-shrink-0 text-primary">✓</span>
                            <span className="ml-3 text-gray-600">Natural language search capabilities for easier queries</span>
                          </li>
                          <li className="flex items-start">
                            <span className="flex-shrink-0 text-primary">✓</span>
                            <span className="ml-3 text-gray-600">Semantic understanding that finds conceptually relevant results</span>
                          </li>
                        </ul>
                      </div>
                    )}
                    
                    {feature.id === 'contract-analysis' && (
                      <div className="mt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">What Our AI Detects</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-primary-50 p-3 rounded-lg">
                            <span className="font-medium text-primary-dark">Risk Clauses</span>
                          </div>
                          <div className="bg-primary-50 p-3 rounded-lg">
                            <span className="font-medium text-primary-dark">Non-Standard Terms</span>
                          </div>
                          <div className="bg-primary-50 p-3 rounded-lg">
                            <span className="font-medium text-primary-dark">Missing Provisions</span>
                          </div>
                          <div className="bg-primary-50 p-3 rounded-lg">
                            <span className="font-medium text-primary-dark">Contradictions</span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {feature.id === 'litigation-prediction' && (
                      <div className="mt-6">
                        <h4 className="text-lg font-medium text-gray-900 mb-3">Predictive Analysis Features</h4>
                        <div className="space-y-4">
                          <div className="bg-primary-50 p-4 rounded-lg">
                            <h5 className="font-medium text-primary-dark mb-2">Case Outcome Estimation</h5>
                            <p className="text-gray-600">Using historical data to predict litigation outcomes with statistical confidence intervals</p>
                          </div>
                          <div className="bg-primary-50 p-4 rounded-lg">
                            <h5 className="font-medium text-primary-dark mb-2">Judge Analysis</h5>
                            <p className="text-gray-600">Insights into how specific judges have ruled on similar matters in the past</p>
                          </div>
                          <div className="bg-primary-50 p-4 rounded-lg">
                            <h5 className="font-medium text-primary-dark mb-2">Settlement Recommendation</h5>
                            <p className="text-gray-600">Data-driven suggestions for optimal settlement timing and amounts</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
        
        <div className="text-center">
          <Button 
            href="/ai-services" 
            variant="primary"
            size="lg"
            className="inline-flex items-center"
          >
            Explore All AI Services
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AIFeatures;